import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  ChevronDown,
  Copy,
  Shield,
  Check,
  CheckCircle2,
  Rocket,
  Power,
  Eye,
  Trash2,
  Loader2,
  PenLine,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { post, del } from '@/lib/api-client'

export interface PolicyCardData {
  id: string
  name: string
  description?: string
  status: 'DRAFT' | 'ACTIVE' | 'INACTIVE'
  humanReadable?: string
  target?: Record<string, unknown>
  rulesSummary?: { permit: number; deny: number }
  algorithm?: string
  xacmlPolicy?: Record<string, unknown>
}

interface PolicyCardProps {
  data: PolicyCardData
  /** When true, renders in preview mode with amber styling and no save/deploy/delete actions */
  isPreview?: boolean
  /** Called when the user clicks "Approve & Save" on a preview card */
  onApprove?: () => void
}

const STATUS_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  DRAFT: {
    bg: 'bg-amber-50 dark:bg-amber-950/50',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
  },
  ACTIVE: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/50',
    text: 'text-emerald-700 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  INACTIVE: {
    bg: 'bg-muted/50 dark:bg-muted/30',
    text: 'text-muted-foreground',
    border: 'border-border',
  },
}

function formatTargetLine(target: Record<string, unknown>): string[] {
  const lines: string[] = []
  const sections = ['Subject', 'Resource', 'Action', 'Environment'] as const

  for (const section of sections) {
    const anyMatch = target[section] ?? target[`${section}s`] ?? target[section.toLowerCase()]
    if (anyMatch && typeof anyMatch === 'object') {
      const entries = Array.isArray(anyMatch) ? anyMatch : [anyMatch]
      for (const entry of entries) {
        if (entry && typeof entry === 'object') {
          const attrId = (entry as Record<string, unknown>).AttributeId ??
            (entry as Record<string, unknown>).attributeId ??
            (entry as Record<string, unknown>).key ?? ''
          const values = (entry as Record<string, unknown>).values ??
            (entry as Record<string, unknown>).AttributeValue ?? ''
          const valStr = Array.isArray(values) ? values.join(', ') : String(values)
          lines.push(`${section}: ${attrId}${valStr ? ` = ${valStr}` : ''}`)
        }
      }
    }
  }
  return lines
}

export function PolicyCard({ data: initialData, isPreview = false, onApprove }: PolicyCardProps) {
  const [data, setData] = useState(initialData)
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [approved, setApproved] = useState(false)

  const statusStyle = isPreview
    ? STATUS_STYLES.DRAFT
    : (STATUS_STYLES[data.status] ?? STATUS_STYLES.DRAFT)
  const targetLines = data.target ? formatTargetLine(data.target) : []
  const xacmlStr = data.xacmlPolicy ? JSON.stringify(data.xacmlPolicy, null, 2) : null

  const handleCopy = () => {
    if (!xacmlStr) return
    navigator.clipboard.writeText(xacmlStr)
    toast.success('XACML copied to clipboard')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDeploy = async () => {
    setActionLoading('deploy')
    try {
      await post(`/policy-drafts/${data.id}/deploy`, {})
      setData({ ...data, status: 'ACTIVE' })
      toast.success('Policy deployed successfully')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Deploy failed'
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleUndeploy = async () => {
    setActionLoading('undeploy')
    try {
      await post(`/policy-drafts/${data.id}/undeploy`, { notes: 'Undeployed via poli-x AI chat' })
      setData({ ...data, status: 'INACTIVE' })
      toast.success('Policy undeployed successfully')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Undeploy failed'
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleDelete = async () => {
    setActionLoading('delete')
    try {
      await del(`/policy-drafts/${data.id}`)
      toast.success('Policy draft deleted')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Delete failed'
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }

  const isLoading = actionLoading !== null

  const handleApprove = () => {
    setApproved(true)
    onApprove?.()
  }

  const borderClass = isPreview
    ? 'border-amber-300 dark:border-amber-700/60'
    : 'border-purple-300 dark:border-purple-700/50'

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className={cn('my-3 rounded-xl border bg-card shadow-sm overflow-hidden', borderClass)}>
        {/* Preview banner */}
        {isPreview && (
          <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800/60">
            <PenLine className="size-3 text-amber-600 dark:text-amber-400" />
            <span className="text-[11px] font-medium text-amber-700 dark:text-amber-300">
              Preview — awaiting your approval before saving
            </span>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={cn(
              'flex items-center justify-center size-7 rounded-lg shrink-0',
              isPreview
                ? 'bg-amber-100 dark:bg-amber-900/50'
                : 'bg-purple-100 dark:bg-purple-900/50'
            )}>
              <Shield className={cn(
                'size-3.5',
                isPreview
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-purple-600 dark:text-purple-400'
              )} strokeWidth={1.5} />
            </div>
            <span className="text-sm font-medium text-foreground truncate">{data.name}</span>
            <Badge
              variant="outline"
              className={cn('text-[10px] px-1.5 py-0 shrink-0 font-semibold', statusStyle.border, statusStyle.text, statusStyle.bg)}
            >
              {isPreview ? 'PREVIEW' : data.status}
            </Badge>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {xacmlStr && (
              <Button variant="ghost" size="sm" className="size-7 p-0" onClick={handleCopy}>
                {copied ? (
                  <Check className="size-3.5 text-green-500" />
                ) : (
                  <Copy className="size-3.5 text-muted-foreground" />
                )}
              </Button>
            )}
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="size-7 p-0">
                <ChevronDown
                  className={cn(
                    'size-3.5 text-muted-foreground transition-transform duration-200',
                    expanded && 'rotate-180'
                  )}
                />
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-2.5">
          {(data.humanReadable ?? data.description) && (
            <p className="text-sm text-foreground dark:text-foreground/70 leading-relaxed">
              {data.humanReadable ?? data.description}
            </p>
          )}

          {targetLines.length > 0 && (
            <div className="rounded-lg bg-muted dark:bg-muted/30 border border-border/70 p-3 space-y-1">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Target</p>
              {targetLines.map((line, i) => (
                <p key={i} className="text-xs text-foreground dark:text-foreground/70 font-mono">{line}</p>
              ))}
            </div>
          )}

          {data.rulesSummary && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>
                Rules: {data.rulesSummary.permit} Permit, {data.rulesSummary.deny} Deny
              </span>
              {data.algorithm && (
                <>
                  <span className="text-border">|</span>
                  <span className="font-mono text-foreground">{data.algorithm}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Expandable XACML */}
        <CollapsibleContent>
          {xacmlStr && (
            <div className="px-4 pb-3">
              <pre className="text-xs bg-muted/80 dark:bg-muted/20 border border-border/70 p-3 rounded-lg overflow-x-auto max-h-96 font-mono leading-relaxed text-foreground">
                {xacmlStr}
              </pre>
            </div>
          )}
        </CollapsibleContent>

        {/* Actions */}
        <div className={cn(
          'flex items-center gap-2 px-4 py-2.5 border-t border-border/60',
          isPreview
            ? 'bg-amber-50/50 dark:bg-amber-950/20'
            : 'bg-muted/20 dark:bg-muted/10'
        )}>
          {isPreview ? (
            <>
              <Button
                size="sm"
                className={cn(
                  'gap-1.5 text-white',
                  approved
                    ? 'bg-emerald-500 dark:bg-emerald-600 cursor-default'
                    : 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600'
                )}
                onClick={handleApprove}
                disabled={approved}
              >
                {approved ? (
                  <Check className="size-3.5" />
                ) : (
                  <CheckCircle2 className="size-3.5" />
                )}
                {approved ? 'Approved' : 'Approve & Save'}
              </Button>
              <CollapsibleTrigger asChild>
                <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground">
                  <Eye className="size-3.5" />
                  {expanded ? 'Hide' : 'View'} XACML
                </Button>
              </CollapsibleTrigger>
            </>
          ) : (
            <>
              {(data.status === 'DRAFT' || data.status === 'INACTIVE') && (
                <Button
                  size="sm"
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white"
                  onClick={handleDeploy}
                  disabled={isLoading}
                >
                  {actionLoading === 'deploy' ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Rocket className="size-3.5" />
                  )}
                  Deploy
                </Button>
              )}
              {data.status === 'ACTIVE' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={handleUndeploy}
                  disabled={isLoading}
                >
                  {actionLoading === 'undeploy' ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Power className="size-3.5" />
                  )}
                  Undeploy
                </Button>
              )}
              <CollapsibleTrigger asChild>
                <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground">
                  <Eye className="size-3.5" />
                  {expanded ? 'Hide' : 'View'} XACML
                </Button>
              </CollapsibleTrigger>
              {data.status !== 'ACTIVE' && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-muted-foreground hover:text-destructive"
                  onClick={handleDelete}
                  disabled={isLoading}
                >
                  {actionLoading === 'delete' ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="size-3.5" />
                  )}
                  Delete
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Collapsible>
  )
}
